import { AwardCategory, ScriptGenre } from '../types';

/**
 * 年度奖项定义表（设计=docs/新增功能-年度奖项与网友评价.md）
 *
 * 两座奖：金枢奖（电影）、星汉奖（电视剧 + 网剧）。短剧与综艺不参评。
 * 每座奖三年一届、彼此错开一年；均为架空命名，⛔ 不得改用任何现实奖项名。
 */
export type AwardId = 'jingshu' | 'xinghan';

export interface AwardDef {
    id: AwardId;
    /** 奖项名 */
    name: string;
    /** 领域显示名 */
    domain: string;
    /** 参评的剧本类型（其余 genre 一律不进候选池） */
    genres: ScriptGenre[];
    /** 类别显示名 */
    categoryNames: Record<AwardCategory, string>;
    /** 获奖永久 Tag（见 data/tables/Tags.ts 的 TAG_3xx 号段） */
    tagIds: Record<AwardCategory, string>;
}

export const AWARDS: AwardDef[] = [
    {
        id: 'jingshu',
        name: '金枢奖',
        domain: '电影',
        genres: ['movie'],
        categoryNames: {
            bestActor: '最佳男主角',
            bestActress: '最佳女主角',
            bestSupportingActor: '最佳男配角',
            bestSupportingActress: '最佳女配角',
        },
        tagIds: {
            bestActor: 'TAG_301',
            bestActress: 'TAG_302',
            bestSupportingActor: 'TAG_303',
            bestSupportingActress: 'TAG_304',
        },
    },
    {
        id: 'xinghan',
        name: '星汉奖',
        domain: '剧集',
        genres: ['tv', 'web'],
        categoryNames: {
            bestActor: '最佳男主角',
            bestActress: '最佳女主角',
            bestSupportingActor: '最佳男配角',
            bestSupportingActress: '最佳女配角',
        },
        tagIds: {
            bestActor: 'TAG_305',
            bestActress: 'TAG_306',
            bestSupportingActor: 'TAG_307',
            bestSupportingActress: 'TAG_308',
        },
    },
];

/** 按 id 索引 */
export const AWARD_MAP: Record<AwardId, AwardDef> = {} as Record<AwardId, AwardDef>;
AWARDS.forEach(a => { AWARD_MAP[a.id] = a; });

/** 四个类别的固定遍历顺序（颁奖夜逐块揭晓的顺序：先配角后主角，压轴留大奖） */
export const AWARD_CATEGORY_ORDER: AwardCategory[] = [
    'bestSupportingActor', 'bestSupportingActress', 'bestActor', 'bestActress',
];

/**
 * 一届颁奖的定义。
 * 届次用显式表而非公式：两奖起点不同（金枢第 1 年就有剧情届）、生涯最后一个月才补金枢末届，
 * 任何 `(year - 1) % 3` 之类的写法都要为这几处开分支，读的人反而看不出排布。
 */
export interface CeremonyDef {
    awardId: AwardId;
    /** 届号（各奖独立顺号，与玩家入行时间无关） */
    edition: number;
    /** 颁奖所在年（0-based，第 N 年 = yearIndex + 1） */
    yearIndex: number;
    /** 颁奖月（1 = 年初；提名恒在颁奖月的上一自然月） */
    month: number;
    /**
     * 剧情届：得主写死，不跑算法、不发收益、不吃作品记录。
     * 只有第 1 年初那届有（玩家尚未入行，是主线剧情的一部分）。
     */
    scripted?: Partial<Record<AwardCategory, { name: string; workName: string }>>;
}

/** 全部届次（按颁奖时间升序） */
export const CEREMONIES: CeremonyDef[] = [
    // 金枢第 23 届：开局即办，玩家只是观众。第 23 届影后 = 苏曼卿（隐退影后，NPC_S001）
    {
        awardId: 'jingshu', edition: 23, yearIndex: 0, month: 1,
        scripted: {
            bestActor: { name: '沈越川', workName: '长夜行舟' },
            bestActress: { name: '苏曼卿', workName: '寒夜信' },
            bestSupportingActor: { name: '罗北望', workName: '长夜行舟' },
            bestSupportingActress: { name: '邵凝霜', workName: '海上堂' },
        },
    },
    // 星汉奖：第 3 / 6 / 9 / 12 / 15 / 18 年年初
    { awardId: 'xinghan', edition: 15, yearIndex: 2, month: 1 },
    { awardId: 'xinghan', edition: 16, yearIndex: 5, month: 1 },
    { awardId: 'xinghan', edition: 17, yearIndex: 8, month: 1 },
    { awardId: 'xinghan', edition: 18, yearIndex: 11, month: 1 },
    { awardId: 'xinghan', edition: 19, yearIndex: 14, month: 1 },
    { awardId: 'xinghan', edition: 20, yearIndex: 17, month: 1 },
    // 金枢奖：第 4 / 7 / 10 / 13 / 16 年年初，末届落在第 19 年 1 月（生涯以电影奖收尾）
    { awardId: 'jingshu', edition: 24, yearIndex: 3, month: 1 },
    { awardId: 'jingshu', edition: 25, yearIndex: 6, month: 1 },
    { awardId: 'jingshu', edition: 26, yearIndex: 9, month: 1 },
    { awardId: 'jingshu', edition: 27, yearIndex: 12, month: 1 },
    { awardId: 'jingshu', edition: 28, yearIndex: 15, month: 1 },
    { awardId: 'jingshu', edition: 29, yearIndex: 18, month: 1 },
];

/** 绝对月份：year*12 + month（month 1-12），与 FilmographyEntry / heatPromoted 同款口径 */
export function absMonthOf(year: number, month: number): number {
    return year * 12 + month;
}

/**
 * 某届的评选窗口 = 上一届（同奖）窗口右端 + 1 → 本届窗口右端。
 * 参评判据是**杀青月**（2026-09-30 用户改定：作品杀青进播放期即合格，⛔ 等播完），
 * 首届从开局第 1 年 1 月算起。
 */
export function windowOf(def: CeremonyDef): { startAbs: number; endAbs: number } {
    return { startAbs: startAbsOf(def), endAbs: endAbsOf(def) };
}

/**
 * 窗口右端（含）= **定格月的前一个自然月月末**（定格月 = 颁奖月的前一个自然月，名单在那个月月初公布）。
 * 2026-09-30 由「颁奖月的前一自然月」提前一个自然月，与播报时机对齐：
 * 1 月颁奖 → 12 月月初公布 → 只收「11 月及更早杀青」的本。
 * ⚠️ 右端⛔ 能停在 12 月末：那样 12 月内才杀青的本按窗口算本届、可公布时它还没杀青，
 *    而下一届左端 = 本届右端 + 1 = 次年 1 月 ⇒ 这批本两届⛔ 算，凭空蒸发。
 *    左端是递推的（= 上一届右端 + 1），所以右端一挪，届与届之间⛔ 缝⛔ 叠。
 * 丙′ v2.3（2026-10-01 茄子拍板「结算时现算」）：收官届右端对齐**生涯末日**（216=40 岁退役那个月的月末）——
 * 收官届⛔ 有下一届（右端外无蒸发面），且提名改在结算时刻现算（⛔ 12 月初定格），12 月杀青的
 * 告別作终于有末届参评资格（修复「归下届而下届不存在」的旧账）。其余届 min 不触发、原样。
 */
export const CAREER_LAST_ABS = 216;

function endAbsOf(def: CeremonyDef): number {
    // 收官届（颁奖 abs 全表最晚=与退役同月、结算时现算的那届）⇒ 右端对齐生涯末日，
    // 12 月杀青告别作可入围；⛔ 只认这一届——其余「各奖最后一届」（如星汉20）仍月初定格消费，
    // 右端若外扩会造出「名单含未杀青作品」的矛盾（定格时它还没杀青）
    const finalAbs = Math.max(...CEREMONIES.filter(c => !c.scripted).map(c => absMonthOf(c.yearIndex, c.month)));
    if (!def.scripted && absMonthOf(def.yearIndex, def.month) === finalAbs) return CAREER_LAST_ABS;
    const end = def.month <= 2
        ? absMonthOf(def.yearIndex - 1, def.month === 1 ? 11 : 12)
        : absMonthOf(def.yearIndex, def.month - 2);
    return Math.min(end, CAREER_LAST_ABS);
}

/** 窗口左端（含）：同奖上一个**评选届**窗口右端 + 1；无则开局首月（剧情届得主写死，不占窗口） */
function startAbsOf(def: CeremonyDef): number {
    const prev = CEREMONIES
        .filter(c => c.awardId === def.awardId && !c.scripted && c.yearIndex < def.yearIndex)
        .sort((a, b) => b.yearIndex - a.yearIndex)[0];
    return prev ? endAbsOf(prev) + 1 : absMonthOf(0, 1);
}

/** 按届号查（存档里只存 awardId + edition，回看往屆时要能取回定义） */
export function findCeremony(awardId: AwardId, edition: number): CeremonyDef | undefined {
    return CEREMONIES.find(c => c.awardId === awardId && c.edition === edition);
}
